/**
 * Settings Service
 * 
 * Handles fetching plugin settings from WordPress backend
 */

import { API_CONFIG, buildWCUrl } from '../../config/api.enhanced';

// Types
export interface PluginSettings {
  booking_display_mode: 'modal' | 'page';
  primary_color: string;
  secondary_color: string;
  success_color: string;
  error_color: string;
  hold_minutes: number;
  default_duration: number;

  // Tracking scripts
  tracking_head_code?: string;
  tracking_body_code?: string;

  // Trip / passenger details behavior
  collect_passenger_details?: number;
  dob_enable_hijri?: number;
  dob_enable_gregorian?: number;
  dob_default_calendar?: 'hijri' | 'gregorian' | string;

  // Contact info requirements (checkout)
  require_contact_name?: number;
  require_contact_email?: number;

  // Registration
  registration_email_required?: number;
}

export interface SettingsResponse {
  success: boolean;
  data: PluginSettings;
}

/**
 * Settings Service Class
 */
class SettingsService {
  private cachedSettings: PluginSettings | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 1 * 60 * 1000; // 1 minute (reduced for faster updates)

  /**
   * Get plugin settings
   * Uses caching to avoid repeated API calls
   */
  async getSettings(forceRefresh: boolean = false): Promise<PluginSettings> {
    // Check cache validity
    const now = Date.now();
    const cacheAge = now - this.cacheTimestamp;
    const isCacheValid = this.cachedSettings && 
                        !forceRefresh && 
                        cacheAge < this.CACHE_DURATION;

    if (isCacheValid) {
      return this.cachedSettings as PluginSettings;
    }

    try {
      // Fetch from API
      // Settings endpoint is under boat-booking/v1 namespace (not wc/v3)
      const url = buildWCUrl('/boat-booking/v1/settings');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data: SettingsResponse = await response.json();
        if (data.success && data.data) {
          this.cachedSettings = data.data;
          this.cacheTimestamp = now;
          return this.cachedSettings;
        }
      }
    } catch (error) {
      // Silently fall through to defaults
    }

    // Fallback to defaults if API fails
    return this.getDefaultSettings();
  }

  /**
   * Get booking display mode specifically
   */
  async getBookingDisplayMode(): Promise<'modal' | 'page'> {
    const settings = await this.getSettings();
    return settings.booking_display_mode;
  }

  /**
   * Clear cache - useful when settings are updated
   */
  clearCache(): void {
    this.cachedSettings = null;
    this.cacheTimestamp = 0;
  }

  /**
   * Get default settings
   */
  private getDefaultSettings(): PluginSettings {
    return {
      booking_display_mode: 'modal',
      primary_color: '#ff7a00',
      secondary_color: '#667eea',
      success_color: '#10b981',
      error_color: '#ef4444',
      hold_minutes: 15,
      default_duration: 30,
    };
  }
}

// Export singleton instance
const settingsService = new SettingsService();
export default settingsService;
